import type {
  FlattenedPlaybookNode,
  PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import { isPlaybookSectionNode } from "@repo/app-types/entities/playbook";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Link } from "@repo/ui/Link/routerLink";
import {
  IconChevronLeft,
  IconChevronRight,
  IconSparkles,
} from "@tabler/icons-react";
import type { FC } from "react";
import { FeedbackReviewJumpToPopover } from "./feedbackReviewJumpToPopover.js";
import { getAdjacentPlaybookNodeIdWithOpenFeedback } from "./feedbackReviewTreeHelpers.js";

/**
 * Strips the standalone button chrome so the three controls read as one pill,
 * and draws the focus ring inward so the pill's overflow-hidden can't clip it.
 */
const joinedButtonClassName =
  "rounded-none shadow-none ring-0 focus-visible:-outline-offset-2";

export interface FeedbackReviewNavigationProps {
  flattenedNodes: readonly FlattenedPlaybookNode[];
  openFeedbackCounts: ReadonlyMap<PlaybookNodeId, number>;
  selectedNodeId: PlaybookNodeId | null;
  onSelectNode: (nodeId: PlaybookNodeId) => void;
  editHref: string;
  onSuggestEdits?: () => void;
}

/**
 * Moves the review tab between criteria: the arrows step to the next node that
 * still has open feedback (never wrapping), while "Jump to" opens the whole
 * tree.
 */
export const FeedbackReviewNavigation: FC<FeedbackReviewNavigationProps> = (
  props,
) => {
  const {
    flattenedNodes,
    openFeedbackCounts,
    selectedNodeId,
    onSelectNode,
    editHref,
    onSuggestEdits,
  } = props;

  const previousNodeId = getAdjacentPlaybookNodeIdWithOpenFeedback(
    flattenedNodes,
    openFeedbackCounts,
    selectedNodeId,
    "previous",
  );
  const nextNodeId = getAdjacentPlaybookNodeIdWithOpenFeedback(
    flattenedNodes,
    openFeedbackCounts,
    selectedNodeId,
    "next",
  );
  const hasOpenFeedback =
    selectedNodeId !== null &&
    (openFeedbackCounts.get(selectedNodeId) ?? 0) > 0;
  const selectedNode = flattenedNodes.find(
    (entry) => entry.node.id === selectedNodeId,
  )?.node;
  const editLinkLabel =
    selectedNode && isPlaybookSectionNode(selectedNode)
      ? "Go to section"
      : "Go to criteria";

  return (
    <div className="flex shrink-0 items-center gap-2">
      <Link
        href={editHref}
        isDisabled={selectedNodeId === null}
        className="mr-2 text-sm font-medium text-foreground hover:text-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      >
        {editLinkLabel}
      </Link>
      <Button
        variant={ButtonVariant.Secondary}
        size={Size.SMALL}
        icon={<IconSparkles className="size-4" />}
        onClick={onSuggestEdits}
        isDisabled={onSuggestEdits === undefined || !hasOpenFeedback}
      >
        Suggest edits
      </Button>
      {/* The Select's display:contents wrapper generates no box, so divide-x
          can't paint around it — the trigger carries its own side borders. */}
      <div className="inline-flex items-center overflow-hidden rounded-lg border border-border-strong bg-white">
        <IconButton
          variant={ButtonVariant.Secondary}
          className={joinedButtonClassName}
          icon={<IconChevronLeft className="size-4" />}
          accessibleLabel="Previous criteria with open feedback"
          isDisabled={previousNodeId === null}
          onClick={() => {
            if (previousNodeId !== null) {
              onSelectNode(previousNodeId);
            }
          }}
        />
        <FeedbackReviewJumpToPopover
          flattenedNodes={flattenedNodes}
          openFeedbackCounts={openFeedbackCounts}
          selectedNodeId={selectedNodeId}
          onSelectNode={onSelectNode}
          isDisabled={flattenedNodes.length === 0}
        />
        <IconButton
          variant={ButtonVariant.Secondary}
          className={joinedButtonClassName}
          icon={<IconChevronRight className="size-4" />}
          accessibleLabel="Next criteria with open feedback"
          isDisabled={nextNodeId === null}
          onClick={() => {
            if (nextNodeId !== null) {
              onSelectNode(nextNodeId);
            }
          }}
        />
      </div>
    </div>
  );
};
